import React from 'react';
import PolicyPageLayout from '../components/PolicyPageLayout';
import { Link } from 'react-router-dom';

const PublicationCharges = () => {
  const sections = [
    {
      title: 'Publication Fee Structure',
      content: (
        <>
          <p>FUWJALS operates as an open access journal and charges Article Processing Charges (APCs) to cover the costs of peer review, editorial services, and online publication. These fees ensure the sustainability of our high-quality publishing services.</p>
          <div className="alert alert-info mt-3">
            <i className="fas fa-info-circle me-2"></i>
            <strong>No submission fees:</strong> Authors are not charged during the submission or review process. Fees are only due after acceptance.
          </div>
        </>
      )
    },
    {
      title: 'Current Fee Schedule',
      content: (
        <>
          <div className="table-responsive">
            <table className="table table-hover table-bordered">
              <thead className="table-primary">
                <tr>
                  <th>Article Type</th>
                  <th>Standard Fee (USD)</th>
                  <th>Developing Countries</th>
                  <th>Students*</th>
                  <th>Page Limit</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Original Research Article</strong></td>
                  <td>$200</td>
                  <td>$150</td>
                  <td>$100</td>
                  <td>15 pages</td>
                </tr>
                <tr>
                  <td><strong>Review Article</strong></td>
                  <td>$250</td>
                  <td>$200</td>
                  <td>$125</td>
                  <td>20 pages</td>
                </tr>
                <tr>
                  <td><strong>Short Communication</strong></td>
                  <td>$150</td>
                  <td>$100</td>
                  <td>$75</td>
                  <td>8 pages</td>
                </tr>
                <tr>
                  <td><strong>Case Study</strong></td>
                  <td>$175</td>
                  <td>$125</td>
                  <td>$90</td>
                  <td>12 pages</td>
                </tr>
                <tr>
                  <td><strong>Technical Note</strong></td>
                  <td>$125</td>
                  <td>$90</td>
                  <td>$65</td>
                  <td>6 pages</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-muted">
            <small>* Student discount requires proof of current enrollment and supervisor co-authorship</small>
          </p>
        </>
      )
    },
    {
      title: 'What Your Fee Covers',
      content: (
        <>
          <div className="row">
            <div className="col-md-6 mb-3">
              <h5 className="text-primary">Editorial Services (40%)</h5>
              <ul className="policy-section-list">
                <li>Editor-in-Chief review and oversight</li>
                <li>Managing Editor coordination</li>
                <li>Section Editor evaluation</li>
                <li>Editorial board consultation</li>
                <li>Quality assurance processes</li>
              </ul>
            </div>
            <div className="col-md-6 mb-3">
              <h5 className="text-primary">Peer Review Process (30%)</h5>
              <ul className="policy-section-list">
                <li>Expert reviewer identification</li>
                <li>Multiple review rounds coordination</li>
                <li>Review quality monitoring</li>
                <li>Reviewer recognition programs</li>
                <li>Statistical review when needed</li>
              </ul>
            </div>
            <div className="col-md-6 mb-3">
              <h5 className="text-primary">Technical Infrastructure (20%)</h5>
              <ul className="policy-section-list">
                <li>Online submission system</li>
                <li>Plagiarism detection software</li>
                <li>Digital publishing platform</li>
                <li>Website hosting and maintenance</li>
                <li>DOI registration and metadata</li>
              </ul>
            </div>
            <div className="col-md-6 mb-3">
              <h5 className="text-primary">Production Services (10%)</h5>
              <ul className="policy-section-list">
                <li>Copy-editing and proofreading</li>
                <li>XML and PDF formatting</li>
                <li>Figure and table optimization</li>
                <li>Reference verification</li>
                <li>Final layout and design</li>
              </ul>
            </div>
          </div>
        </>
      )
    },
    {
      title: 'Fee Waivers and Discounts',
      content: (
        <>
          <div className="alert alert-success mb-4">
            <h5><i className="fas fa-gift me-2"></i>Discount Programs Available</h5>
            <p className="mb-0">
              FUWJALS is committed to supporting researchers from all economic backgrounds through our comprehensive discount and waiver programs.
            </p>
          </div>

          <h5>Automatic Discounts</h5>
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="bg-light p-3 rounded mb-3">
                <h6 className="text-primary">Developing Countries (25% Off)</h6>
                <p>Authors from World Bank classified lower-middle-income countries:</p>
                <ul className="small">
                  <li>Nigeria, Ghana, Kenya, Bangladesh</li>
                  <li>India, Pakistan, Egypt, Morocco</li>
                  <li>And 50+ other qualifying countries</li>
                </ul>
              </div>
            </div>
            <div className="col-md-6">
              <div className="bg-light p-3 rounded mb-3">
                <h6 className="text-primary">Students (50% Off)</h6>
                <p>Full-time students with:</p>
                <ul className="small">
                  <li>Proof of current enrollment</li>
                  <li>Faculty supervisor as co-author</li>
                  <li>Valid student ID or letter</li>
                </ul>
              </div>
            </div>
          </div>

          <h5>Complete Fee Waivers</h5>
          <p>100% fee waivers available for:</p>
          <ul className="section-list">
            <li><strong>Least Developed Countries (LDCs):</strong> Authors from UN-classified LDCs</li>
            <li><strong>Invited Articles:</strong> Solicited contributions from editorial board</li>
            <li><strong>Financial Hardship:</strong> Case-by-case evaluation with documentation</li>
            <li><strong>Institutional Partners:</strong> Collaboration agreement institutions</li>
            <li><strong>Special Issues:</strong> Selected contributions to thematic issues</li>
          </ul>

          <div className="mt-3">
            <h6>How to Apply</h6>
            <p>
              Submit waiver applications to: <a href="mailto:fee-waiver@fuwukari.edu.ng">fee-waiver@fuwukari.edu.ng</a><br />
              Include: documentation, article details, and justification letter.
            </p>
          </div>
        </>
      )
    },
    {
      title: 'Payment Timeline',
      content: 'No fees are charged during submission or review. Payment is only required after acceptance.',
      list: [
        'Manuscript Submission: No fee required during submission',
        'Editorial Review: Initial review and peer review process (free)',
        'Conditional Acceptance: Fee invoice sent with acceptance notification',
        'Payment Due: Payment required within 30 days of acceptance',
        'Publication: Article published after payment confirmation'
      ],
      highlight: (
        <>
          <i className="fas fa-exclamation-triangle me-2"></i>
          Articles will not be published until payment is received and confirmed. Extensions may be granted in exceptional circumstances.
        </>
      )
    },
    {
      title: 'Additional Charges',
      content: (
        <>
          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="table-secondary">
                <tr>
                  <th>Service</th>
                  <th>Fee (USD)</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Page Excess Fee</td>
                  <td>$25 per page</td>
                  <td>For articles exceeding standard page limits</td>
                </tr>
                <tr>
                  <td>Color Figure Printing</td>
                  <td>$50 per figure</td>
                  <td>Enhanced color reproduction (optional)</td>
                </tr>
                <tr>
                  <td>Rush Processing</td>
                  <td>$100</td>
                  <td>Expedited review and publication (subject to availability)</td>
                </tr>
                <tr>
                  <td>English Language Editing</td>
                  <td>$150</td>
                  <td>Professional language editing service (optional)</td>
                </tr>
                <tr>
                  <td>Extensive Revision</td>
                  <td>$75</td>
                  <td>Major formatting and structural changes</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-muted mt-2">
            <small>Additional charges are optional and will be communicated before being applied</small>
          </p>
        </>
      )
    },
    {
      title: 'Payment Methods',
      content: (
        <>
          <div className="row">
            <div className="col-md-6">
              <h5 className="text-primary">International Authors</h5>
              <ul className="policy-section-list">
                <li><i className="fab fa-paypal me-2"></i>PayPal (recommended)</li>
                <li><i className="fas fa-credit-card me-2"></i>Credit/Debit Cards (Visa, MasterCard, AMEX)</li>
                <li><i className="fas fa-university me-2"></i>Bank Wire Transfer</li>
                <li><i className="fas fa-money-bill-wave me-2"></i>Western Union, MoneyGram</li>
              </ul>
            </div>
            <div className="col-md-6">
              <h5 className="text-primary">Nigerian Authors</h5>
              <ul className="policy-section-list">
                <li><i className="fas fa-mobile-alt me-2"></i>Mobile Money (MTN, Airtel)</li>
                <li><i className="fas fa-building me-2"></i>Bank Transfer (Naira)</li>
                <li><i className="fas fa-laptop me-2"></i>Online Banking</li>
                <li><i className="fas fa-money-check-alt me-2"></i>Bank Draft</li>
              </ul>
            </div>
          </div>

          <div className="text-center mt-4">
            <Link to="/payment-methods" className="btn btn-outline-primary">
              View Detailed Payment Information
            </Link>
          </div>
        </>
      )
    },
    {
      title: 'Fee Comparison',
      content: (
        <>
          <h5>FUWJALS vs. Similar Journals</h5>
          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="table-info">
                <tr>
                  <th>Journal Category</th>
                  <th>Average Fee (USD)</th>
                  <th>FUWJALS Fee</th>
                  <th>Savings</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Agriculture Journals</td>
                  <td>$2,000 - $3,500</td>
                  <td>$200</td>
                  <td className="text-success"><strong>90% less</strong></td>
                </tr>
                <tr>
                  <td>Life Sciences Journals</td>
                  <td>$2,500 - $4,000</td>
                  <td>$200</td>
                  <td className="text-success"><strong>92% less</strong></td>
                </tr>
                <tr>
                  <td>Regional Open Access</td>
                  <td>$500 - $1,200</td>
                  <td>$200</td>
                  <td className="text-success"><strong>60-80% less</strong></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="alert alert-success mt-3">
            <h6><i className="fas fa-piggy-bank me-2"></i>Affordable Excellence</h6>
            <p className="mb-0">
              FUWJALS offers world-class publishing services at a fraction of the cost of comparable international journals, making quality research accessible globally.
            </p>
          </div>
        </>
      )
    },
    {
      title: 'Institutional Accounts',
      content: (
        <>
          <h5>Bulk Payment Options</h5>
          <p>Universities and research institutions can establish accounts for streamlined payment processing and institutional billing.</p>

          <div className="row">
            <div className="col-md-6">
              <div className="bg-light p-3 rounded">
                <h6 className="text-primary">Benefits</h6>
                <ul className="policy-section-list">
                  <li>Volume discounts (5-20% off)</li>
                  <li>Centralized billing</li>
                  <li>Extended payment terms</li>
                  <li>Monthly/quarterly invoicing</li>
                  <li>Dedicated account manager</li>
                </ul>
              </div>
            </div>
            <div className="col-md-6">
              <div className="bg-light p-3 rounded">
                <h6 className="text-primary">Requirements</h6>
                <ul className="policy-section-list">
                  <li>Minimum 10 articles per year</li>
                  <li>Institutional agreement</li>
                  <li>Authorized signatory</li>
                  <li>Purchase order system</li>
                  <li>Annual volume commitment</li>
                </ul>
              </div>
            </div>
          </div>

          <p className="mt-3">
            Contact: <a href="mailto:institutional@fuwukari.edu.ng">institutional@fuwukari.edu.ng</a>
          </p>
        </>
      )
    },
    {
      title: 'Frequently Asked Questions',
      content: (
        <>
          <div className="mb-3">
            <h6 className="text-primary">Q: When do I need to pay the publication fee?</h6>
            <p>A: Payment is required only after your manuscript is accepted for publication. No fees are charged during submission or review.</p>
          </div>

          <div className="mb-3">
            <h6 className="text-primary">Q: Are fees refundable?</h6>
            <p>A: Fees are generally non-refundable as they cover services already provided. Refunds may be considered in exceptional circumstances.</p>
          </div>

          <div className="mb-3">
            <h6 className="text-primary">Q: Can I pay in local currency?</h6>
            <p>A: Yes, we accept payments in Nigerian Naira and other major currencies. Exchange rates are updated daily.</p>
          </div>

          <div className="mb-3">
            <h6 className="text-primary">Q: What if I cannot afford the publication fee?</h6>
            <p>A: We offer various discount programs and fee waivers. Contact our fee waiver office for assistance.</p>
          </div>

          <div className="mb-3">
            <h6 className="text-primary">Q: Are there any hidden costs?</h6>
            <p>A: No hidden costs. All potential additional charges are clearly communicated before being applied.</p>
          </div>
        </>
      )
    },
    {
      title: 'Contact Information',
      content: 'For assistance with publication fees and payment options:',
      highlight: (
        <>
          <div className="row">
            <div className="col-md-6">
              <strong>Publication Fees</strong><br />
              Email: fees@fuwukari.edu.ng<br />
              Phone: +234 XXX XXX XXXX<br />
              Hours: Mon-Fri, 9 AM - 5 PM WAT
            </div>
            <div className="col-md-6">
              <strong>Fee Waivers</strong><br />
              Email: fee-waiver@fuwukari.edu.ng<br />
              Response time: 5-7 business days<br />
              Decision notification: Email
            </div>
          </div>

          <div className="text-center mt-4">
            <Link to="/submit-paper" className="btn btn-primary me-3">Submit Your Manuscript</Link>
            <Link to="/payment-methods" className="btn btn-outline-secondary">Payment Details</Link>
          </div>
        </>
      )
    }
  ];

  return (
    <PolicyPageLayout
      title="Publication Charges"
      sections={sections}
      icon="💰"
    />
  );
};

export default PublicationCharges;